import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Trophy, User, Heart, Menu, X, Settings, Swords, Users, Lock, Target } from "lucide-react";
import { useAuth } from "../lib/auth";
import StudyMateLogo from "./StudyMateLogo";
import UserAvatar from "./UserAvatar";
import SettingsModal from "./SettingsModal";

const MOBILE_NAV = [
  { to: "/casual", label: "Casual", icon: Swords },
  { to: "/multiplayer", label: "Multiplayer", icon: Users },
  { to: "/private", label: "Private", icon: Lock },
  { to: "/practice", label: "Practice", icon: Target },
];

export default function Header() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const onAuth = loc.pathname === "/login" || loc.pathname === "/signup";
  if (onAuth) return null;

  const closeMenu = () => setMenuOpen(false);

  const go = (path) => {
    closeMenu();
    nav(path);
  };

  const openSettings = () => {
    closeMenu();
    if (user) setSettingsOpen(true);
    else nav("/login");
  };

  return (
    <>
      <header className="border-b border-white/8 bg-[#02040D]/85 backdrop-blur sticky top-0 z-40" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="home-logo">
            <StudyMateLogo compact />
          </Link>

          {/* Desktop nav — unchanged */}
          <nav className="hidden sm:flex items-center gap-2 sm:gap-3 ml-auto">
            <Link to="/leaderboard" className="flex items-center gap-2 font-mono text-[11px] sm:text-[12px] tracking-[0.3em] uppercase text-soft hover:text-white" data-testid="nav-leaderboard">
              <Trophy size={14} /> <span>LEADERBOARD</span>
            </Link>
            <Link
              to="/donate"
              className="hidden md:flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] uppercase text-cyan-400 hover:text-cyan-300 px-3 py-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/5 hover:bg-cyan-500/10 transition-all"
              data-testid="nav-donate"
            >
              <Heart size={14} className="animate-pulse" /> SUPPORT
            </Link>
            {user ? (
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Link
                  to={`/profile/${user.username}`}
                  className="flex items-center gap-1 sm:gap-2 font-mono text-[11px] sm:text-[12px] tracking-[0.3em] uppercase text-soft hover:text-white"
                  data-testid="nav-profile"
                >
                  <UserAvatar src={user.avatar_url} size={24} />
                  <span>{user.username}</span>
                </Link>
                <button
                  onClick={openSettings}
                  className="p-2 rounded-lg text-soft hover:text-white hover:bg-white/5 transition-colors"
                  data-testid="nav-settings"
                  aria-label="Settings"
                  title="Settings"
                >
                  <Settings size={18} />
                </button>
              </div>
            ) : (
              <button onClick={() => nav("/login")} className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-blue-500/40 bg-blue-500/10 text-blue font-mono text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.3em] uppercase hover:bg-blue-500/15 whitespace-nowrap flex-shrink-0" data-testid="nav-signin">
                SIGN IN
              </button>
            )}
          </nav>

          {/* Mobile nav — leaderboard icon + hamburger */}
          <div className="flex sm:hidden items-center gap-1 ml-auto">
            <Link
              to="/leaderboard"
              className="p-2 rounded-lg text-soft hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-leaderboard-mobile"
              aria-label="Leaderboard"
            >
              <Trophy size={20} />
            </Link>
            {user && (
              <button
                onClick={openSettings}
                className="p-2 rounded-lg text-soft hover:text-white hover:bg-white/5 transition-colors"
                data-testid="nav-settings-mobile"
                aria-label="Settings"
              >
                <Settings size={20} />
              </button>
            )}
            <button
              onClick={() => setMenuOpen(true)}
              className="p-2 rounded-lg text-soft hover:text-white hover:bg-white/5 transition-colors"
              data-testid="nav-menu-toggle"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile slide-out menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 sm:hidden" data-testid="mobile-nav-menu">
          <button
            className="absolute inset-0 bg-[#02040D]/80 backdrop-blur-sm"
            onClick={closeMenu}
            aria-label="Close menu"
          />
          <div className="absolute right-0 top-0 bottom-0 w-[min(85vw,320px)] bg-[#0B0C14] border-l border-white/10 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-4 h-14 border-b border-white/8">
              <span className="font-mono text-[10px] tracking-[0.35em] text-blue uppercase">Menu</span>
              <button onClick={closeMenu} className="p-2 text-soft hover:text-white" aria-label="Close menu">
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {user ? (
                <button
                  onClick={() => go(`/profile/${user.username}`)}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/5 text-left"
                  data-testid="mobile-nav-profile"
                >
                  <UserAvatar src={user.avatar_url} size={32} />
                  <div>
                    <div className="font-heading font-bold text-sm">{user.username}</div>
                    <div className="font-mono text-[10px] tracking-widest text-soft uppercase">Profile</div>
                  </div>
                </button>
              ) : (
                <button
                  onClick={() => go("/login")}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/15 text-left"
                  data-testid="mobile-nav-signin"
                >
                  <User size={18} className="text-blue" />
                  <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-blue">Sign In</span>
                </button>
              )}

              <button
                onClick={() => go("/donate")}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/5 text-left"
                data-testid="mobile-nav-support"
              >
                <Heart size={18} className="text-cyan-400" />
                <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-soft">Support Us</span>
              </button>

              <button
                onClick={openSettings}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/5 text-left"
                data-testid="mobile-nav-settings"
              >
                <Settings size={18} className="text-soft" />
                <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-soft">Settings</span>
              </button>

              <div className="my-3 border-t border-white/8" />

              {MOBILE_NAV.map(({ to, label, icon: Icon }) => (
                <button
                  key={to}
                  onClick={() => go(to)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 text-left"
                >
                  <Icon size={16} className="text-blue" />
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-soft">{label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}
